import { useEffect, useState } from "react";
import { ArrowLeft, Instagram, Music2, Upload, Youtube } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";
import { mediaUrl } from "../../lib/media";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", Icon: Instagram },
  { id: "tiktok", label: "TikTok", Icon: Music2 },
  { id: "youtube", label: "YouTube", Icon: Youtube },
];

const EMPTY = { platform: "instagram", url: "", caption: "", cover: "", published: true };

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-paper placeholder:text-paper/30 transition-colors focus:border-acid focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50";

export default function PostEditor({ postId, onBack }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(!postId);

  useEffect(() => {
    if (!postId) return;
    adminFetch("/api/admin/posts")
      .then((r) => r.json())
      .then((list) => {
        const p = Array.isArray(list) ? list.find((x) => x.id === postId) : null;
        if (p) setForm({ ...EMPTY, ...p });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [postId]);

  const onPickCover = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setStatus("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      const { path } = await res.json();
      setForm((f) => ({ ...f, cover: path }));
    } catch {
      setStatus("Upload failed — try again");
    }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    setStatus("");
    const res = await adminFetch(postId ? `/api/admin/posts/${postId}` : "/api/admin/posts", {
      method: postId ? "PUT" : "POST",
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (res.ok) {
      onBack();
      return;
    }
    const data = await res.json().catch(() => ({}));
    setStatus(typeof data.detail === "string" ? data.detail : "Save failed — check the fields");
  };

  if (!loaded) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-ink">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-paper/50">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="post-editor" className="min-h-svh bg-ink text-paper">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-5 sm:px-6">
          <button
            type="button"
            data-testid="post-editor-back"
            onClick={onBack}
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 transition-colors hover:text-acid"
          >
            <ArrowLeft className="h-4 w-4" /> Feed Posts
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-paper/50">
            {postId ? "Edit Post" : "New Post"}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <div>
          <span className={labelCls}>Platform *</span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Platform">
            {PLATFORMS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                data-testid={`post-platform-${id}`}
                onClick={() => setForm((f) => ({ ...f, platform: id }))}
                aria-pressed={form.platform === id}
                className={`flex items-center gap-2 border px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  form.platform === id
                    ? "border-acid bg-acid text-ink"
                    : "border-white/15 text-paper/60 hover:border-white/40 hover:text-paper"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="post-url" className={labelCls}>Link to the Post *</label>
          <input
            id="post-url"
            data-testid="post-url-input"
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            className={inputCls}
            placeholder="https://www.instagram.com/p/…"
          />
          <p className="mt-2 font-mono text-[10px] leading-relaxed text-paper/40">
            Paste the link right after you publish on the platform — the card on your site opens it.
          </p>
        </div>

        <div className="mt-6">
          <label htmlFor="post-caption" className={labelCls}>Caption</label>
          <textarea
            id="post-caption"
            data-testid="post-caption-input"
            rows={3}
            value={form.caption}
            onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
            className={`${inputCls} resize-y`}
            placeholder="What shows under the post card on your site"
          />
        </div>

        <div className="mt-6">
          <span className={labelCls}>Cover Image</span>
          {form.cover && (
            <img
              src={mediaUrl(form.cover)}
              alt="Post cover"
              className="mb-3 aspect-[4/5] w-full max-w-xs border border-white/10 object-cover"
            />
          )}
          <label
            data-testid="post-cover-upload"
            className="flex w-fit cursor-pointer items-center gap-2 border border-white/15 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70 transition-colors hover:border-acid hover:text-acid"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? "Uploading…" : form.cover ? "Replace Cover" : "Upload Cover"}
            <input type="file" accept="image/*" className="hidden" onChange={onPickCover} />
          </label>
          <p className="mt-2 font-mono text-[10px] leading-relaxed text-paper/40">
            Screenshot a frame of the video or use the post's image — vertical looks best.
          </p>
        </div>

        <div className="mt-6">
          <button
            type="button"
            role="switch"
            aria-checked={form.published}
            data-testid="post-published-toggle"
            onClick={() => setForm((f) => ({ ...f, published: !f.published }))}
            className={`flex items-center gap-3 border px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              form.published
                ? "border-acid bg-acid/10 text-acid"
                : "border-white/15 text-paper/50 hover:border-white/40"
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${form.published ? "bg-acid" : "bg-paper/30"}`} aria-hidden="true" />
            Visible on the feed
          </button>
        </div>

        {status && (
          <p data-testid="post-editor-status" className="mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-red-400">
            {status}
          </p>
        )}

        <button
          type="button"
          data-testid="post-save-button"
          onClick={save}
          disabled={saving || uploading || !form.url}
          className="mt-10 w-full bg-acid py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-paper disabled:opacity-40 sm:w-auto sm:px-12"
        >
          {saving ? "Saving…" : postId ? "Save Changes" : "Add to Feed"}
        </button>
      </main>
    </div>
  );
}
